import $ from 'jquery';
import _ from 'underscore';
import BarTimerHtml from '../../html/barTimer.html';
import BaseGameViewHtml from '../../html/baseGameView.html';
import EndScreenHtml from '../../html/endscreenDynamic.html';
import FlourishTemplate from '../../html/genericFlourish.html';
import InstructionsHtml from '../../html/genericInstructions.html';
import BaseView from '../baseView';
import Environment from '../environment';
import { createButtons } from '../genericButton';
import AudioManager from '../manager/audioManager';
import { SOUNDS } from '../manager/audioManager.types';
import InputManager from '../manager/inputManager';
import { SOCIAL_SHARE_REGEX } from '../manager/socialShareManager';
import { msToMinutesSeconds } from '../util/utilities';
import Arcade from './arcade/arcade';
import BaseGame from './baseGame';
import { EVENTS } from './baseGame.types';
import BaseRound from './baseRound';
import { EVENTS as GAMECLOCK_EVENTS } from './gameClock';
import { ATTRIBUTES, SCORE_TYPES } from './trivia/trivia.types';

/**
 * @typedef {import('./baseGame.types').BaseGameOptions} BaseGameOptions
 * @typedef {import('./baseGame.types').BaseRoundOptions} BaseRoundOptions
 * @typedef {import('./baseGame.types').BaseGameViewOptions} BaseGameViewOptions
 * @typedef {import('./baseGame.types').FlourishOptions} FlourishOptions
 */

/**
 * @class
 * @template {BaseGame<BaseGameOptions, BaseRoundOptions, BaseRound<BaseRoundOptions>>} GameType
 * @extends BaseView<BaseGameViewOptions>
 */
export default class BaseGameView extends BaseView {
	/**
	 * Returns the class name of the BaseGameView class.
	 * @returns {string} The class name of the BaseGameView class.
	 */
	get CN() {
		return BaseGameView.name;
	}

	/**
	 * Returns the BaseGame class that this BaseGameView is associated with.
	 * @returns {typeof GameType} The BaseGame class.
	 */
	get GameClass() {
		return BaseGame;
	}

	/**
	 * Returns a compiled Underscore.js template for the generic flourish HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get flourishTemplate() {
		return _.template(FlourishTemplate);
	}

	/**
	 * Returns a compiled Underscore.js template for the bar timer HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get barTimerTemplate() {
		return _.template(BarTimerHtml);
	}

	/**
	 * Returns a compiled Underscore.js template for the instructions HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get instructionsTemplate() {
		return _.template(InstructionsHtml);
	}

	/**
	 * Returns a compiled Underscore.js template for the base game view HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get baseGameViewTemplate() {
		return _.template(BaseGameViewHtml);
	}

	/**
	 * Returns a compiled Underscore.js template for the round view HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get roundTemplate() {
		return _.template('');
	}

	/**
	 * Returns a compiled Underscore.js template for the endscreen view HTML.
	 * @returns {_.CompiledTemplate} The compiled template.
	 */
	get endscreenTemplate() {
		return _.template(EndScreenHtml);
	}

	/**
	 * Defines the event handlers for the BaseGameView.
	 * @returns {import('backbone').EventsHash} An object mapping event names to their corresponding handler functions.
	 */
	events() {
		return {
			'click .progress-indicator .total': this.skipToEnd,
			'click .progress-indicator .current': this.nextRound,
			'click .btn-start': this.startGame,
			'click .btn.reset': this.restartGame,
			'click .timer': this.toggleClock,
		};
	}

	/**
	 * @param {BaseGameViewOptions} options - The options to initialize the BaseView instance with.
	 */
	constructor(options) {
		super(options);
		this.logger.log(`[BaseGameView.constructor]`, { ...options });

		/**
		 * @type {GameType}
		 * @description This is here simply to inform the typing system that this.game will always be a available
		 * because it does not pick up on the fact that this.game is create in this.initialize which is called in
		 * the Backbone.View constructor.
		 */
		this.game = this.game || new this.GameClass(options);
	}

	/** @returns {boolean} Whether or not to add a visual bar timer. */
	get hasBarTimer() {
		return (
			(this.game.clock || false) &&
			Number.isFinite(this.game.clock?.timeLimit) &&
			!!this.game.get('timerOrientation')
		);
	}

	/**
	 * Initialization method that sets up common listeners
	 * @param {BaseGameViewOptions} options Data to be used by the game.
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		this.$el.addClass('loading');

		this.game?.destroy();
		this.game = new this.GameClass(options);
		this.game
			.once({
				[EVENTS.initialized]: this.onInitialized.bind(this),
			})
			.on({
				[EVENTS.gameRound]: (round, roundIndex) => {
					this.onRound(round, roundIndex);
				},
				[EVENTS.gameComplete]: () => {
					this.displayEndScreen();
				},
			});

		this.skipToEnd = this.skipToEnd.bind(this);
		InputManager.on('9', this.skipToEnd);

		super.initialize(options);
	}

	/**
	 * Handles initialization of the game view, including setting up clock listeners and displaying instructions.
	 * @param {GameType} game - The game instance associated with this view.
	 */
	onInitialized(game) {
		this.logger.log(`[${this.onInitialized.name}]`, { game });

		if (this.game.get('timeLimit')) {
			this.setupClockListeners();
		}

		this.$el.removeClass('loading');
		this.displayInstructions();
	}

	setupClockListeners() {
		this.game.clock
			?.on(GAMECLOCK_EVENTS.tick, this.updateTimer.bind(this))
			.on(GAMECLOCK_EVENTS.timeLow, this.onTimeLow.bind(this))
			.on(GAMECLOCK_EVENTS.timeExpired, this.onTimeExpired.bind(this))
			.on(GAMECLOCK_EVENTS.reset, this.onTimerReset.bind(this));
	}

	/**
	 * Renders the instructions template provided by the game data.
	 * @returns {string} Rendered instructions HTML to be inserted into the DOM
	 */
	renderInstructions() {
		this.logger.log(`[${this.renderInstructions.name}]`);

		const instructionData = this.game.get('instructions') || {};
		return this.instructionsTemplate(instructionData);
	}

	/**
	 * Displays default instruction template with values provided from data. Calls
	 * startGame() function if no instructions data is provided.
	 * @returns {Promise<void>} A promise for the convenience of chaining
	 */
	displayInstructions() {
		this.logger.log(`[${this.displayInstructions.name}]`);

		if (this.game?.has('instructions')) {
			return this.replaceView(this.renderInstructions(), () => {
				this.$('.instructions').append(
					createButtons({
						text: ['START'],
						className: 'btn-start',
						...this.game.get('instructions')?.btn,
					}),
				);
				/**
				 * Set timeout to wait 1 cycle before trying to focus the start button
				 */
				this.wait().then(() => {
					this.attachDecorators(); // needs to be called after adding the buttons
					InputManager.focus(this.$('.btn-start').get(0));
				});
			}).then(() => {
				const btnStart = this.$('.btn-start').get(0);
				if (btnStart) InputManager.focus(btnStart);
			});
		}

		this.logger.info('No instructions provided. Starting game immediately.');
		return Promise.resolve()
			.then(() => this.startGame())
			.catch((error) => this.logger.error('[Failed to start game]', { error }));
	}

	/**
	 * Dismisses the view, calls the render function, triggers next round, then updates timer if needed.
	 * @returns {Promise<any>}
	 */
	startGame() {
		this.logger.log(`[${this.startGame.name}]`);

		this.game.sendGA4Event('GameStart');

		return this.dismissView()
			.then(() => this.render())
			.then(() => this.game.nextRound())
			.then(() => this.updateTimer());
	}

	/**
	 * Renders the BaseGameView template with game attributes if they exist and adds it to the current view.
	 * @returns {Promise<any>}
	 */
	render() {
		this.logger.log(`[${this.render.name}]`);

		this.$el.empty().append(this.baseGameViewTemplate({ ...this.game.attributes }));

		this.attachDecorators();

		return Promise.resolve(this);
	}

	/**
	 * Triggers next round.
	 * For debugging purposes only in non production environments.
	 */
	nextRound() {
		if (!Environment.isProd() && this.game && this.game.rounds) {
			this.logger.log(`[${this.nextRound.name}]`);
			AudioManager.playSound('triviaCorrect');
			this.game.nextRound();
		}
	}

	/**
	 *	Adds a flourish to the container and dismisses it after the specified duration (in milliseconds)
	 * @param {FlourishOptions} flourishData Flourish data {id, duration, message, imgUrl}
	 * @param {string|JQuery} container element to append template to
	 * @returns {Promise<any>} A promise that will be resolved when the flourish is complete
	 */
	displayFlourish(flourishData, container = '.content-panel') {
		this.logger.log(`[${this.displayFlourish.name}]`, { flourishData, container });
		container = container instanceof $ ? container : this.$(/** @type {string} */ (container));

		// Prepend populated Flourish Template element from supplied data
		const flourishEl = this.flourishTemplate(flourishData);
		container.prepend(flourishEl);

		// Wait for duration and then remove Flourish element
		const { id, duration = 1000 } = flourishData;
		return this.wait(duration).then(() => container.find(`.flourish.${id}`).remove());
	}

	/**
	 * Starts a countdown and displays flourishes in a container.
	 * @param {string} container The container to add the countdown to.
	 */
	displayCountDown(container = '.content-panel') {
		this.logger.log(`[${this.displayCountDown.name}]`, { container });

		// Generate array of our countdown numbers from the game
		const { countDown = 0, countValues = undefined } = this.game.attributes;
		const countdownValues = countValues || _.range(parseFloat(`${countDown}`), 0); // *Note: _.range() excludes the 'stop' parameter

		// No count down parameters provided. Instantly resolve.
		if (!countdownValues?.length) {
			return Promise.resolve();
		}

		// append new container to supplied container
		const countDownContainer = $(`<div id="countDown-container"></div>`);
		this.dismissElement(countDownContainer, { duration: 0 }).then(() =>
			this.$(container).prepend(countDownContainer),
		);

		// Uses reduce to create a promise chain that triggers a flourish for each count
		const originalPromise = this.displayElement(countDownContainer);
		return countdownValues
			.reduce((prevCountPromise, currentCount) => {
				// Generate flourish data
				const flourishData = {
					id: `countDown`,
					duration: 1000,
					message: currentCount,
				};

				// Wait for the previous flourish then do this one
				return prevCountPromise.then(() =>
					this.displayFlourish(flourishData, countDownContainer),
				);
			}, originalPromise)
			.then(() => this.dismissElement(countDownContainer))
			.then(() => this.$(container).find('#countDown-container').remove());
	}

	/**
	 * Adds a visual-based bar timer to the engine if applicable.
	 * [Preconditions] A valid game with a valid (downwards counting)
	 * clock, a 'timerOrientation' in the game's attributes, and
	 * the absense of a bar timer on this.$el.
	 *
	 * @param {string|Element|JQuery<HTMLElement>} [target] Element as reference for bar timer placement
	 * @param {boolean} [isSibling] Timer is a sibling or a child
	 * If omitted, inserts timer as the last child of [target]
	 * If true, inserts timer as the next sibling of [target]
	 */
	insertBarTimer(target = this.$('.body'), isSibling = false) {
		this.logger.log(`[${this.insertBarTimer.name}]`, { target, isSibling });

		if (!this.hasBarTimer || this.$('.bar-timer').length) return null;

		// * Convert to JQuery.
		if (!(target instanceof $)) target = this.$(target);

		const timerHtml = this.barTimerTemplate(this.game.attributes);

		return isSibling ? target.after(timerHtml) : target.append(timerHtml);
	}

	/**
	 * Replaces the current content-panel body with a rendered round.
	 * @param {RoundType} round The round data to use for rendering the current round
	 * @returns {Promise<any>} Resolves when the round has been displayed
	 */
	onRound(round, onReplaced = this.setProgressIndicator.bind(this)) {
		this.logger.log(`[${this.onRound.name}]`, { round });

		const hasBody = this.$('.body').children().length > 0;
		const duration = hasBody ? undefined : 0;
		// We assume that if .body has no children then the entire view is changing and

		const roundView = this.roundTemplate?.({ ...round?.toJSON() });
		return this.replaceElement('.body', roundView, onReplaced, { duration }).then(() =>
			this.displayCountDown(),
		);
	}

	/**
	 * Updates the progress indicator in the content-panel header section with a current and total value.
	 * @param {number} [current] The value to display for 'current' round value. Will default to the
	 * current round index + 1.
	 * @param {number} [total] The value to display for total rounds. Will default to the total number
	 * or rounds.
	 */
	setProgressIndicator(current, total) {
		this.logger.log(`[${this.setProgressIndicator.name}]`, { current, total });

		let currVal = current ?? '';
		let totalVal = total ?? '';

		if (this.game) {
			// Only update if not already set via parameters
			currVal ||= this.game.roundIndex() + 1;
			totalVal ||= this.game.rounds.length;
		}

		currVal = currVal.toString();
		totalVal = totalVal.toString();

		const $current = this.$('.progress-indicator .current');
		const $total = this.$('.progress-indicator .total');

		if ($current.text() !== currVal || $total.text() !== totalVal) {
			this.replaceElement('.progress-indicator .current', currVal);
			this.replaceElement('.progress-indicator .total', totalVal);
		}
	}

	/**
	 * Updates the timer in the content-panel header with the value from the gameClock.
	 */
	updateTimer(elapsedTime = 0) {
		this.logger.debug(`[${this.updateTimer.name}]`, { elapsedTime });

		if (!this.game.clock) {
			this.logger.debug(
				`Game "${this.game.get('gameId')}" does not have a clock configured...`,
			);
			return;
		}

		const hasTimeLimit = Number.isFinite(this.game.clock.timeLimit);
		const newTimerText = msToMinutesSeconds(
			hasTimeLimit ? this.game.clock.getRemainingTime() : elapsedTime,
		);

		// Only update timer in this .content-panel .header and not any internal .content-panel .header
		const headerTimer = this.$('> .content-panel > .header .timer .timer-text');
		// Reduce DOM churn by checking current value actually needs updating
		if (headerTimer.text() !== newTimerText) {
			headerTimer.text(newTimerText);
		}

		// * Update visual timer if needed
		const timerOrientation = this.game.get('timerOrientation');
		const visualBarTimer = this.$('.timer-progress-bar');
		if (hasTimeLimit && timerOrientation && visualBarTimer.length) {
			const completionPercentage = Math.max(elapsedTime / this.game.clock.timeLimit, 0) * 100;
			const remainderPercentage = parseFloat(`${100 - completionPercentage}`).toFixed(1);

			if (remainderPercentage !== this.currentTimerPercentage) {
				if (timerOrientation === 'horizontal') {
					visualBarTimer.css({ width: `${remainderPercentage}%` });
				} else {
					visualBarTimer.css({ height: `${remainderPercentage}%` });
				}

				// Cache currentTimerPercentage to avoid updating or querying the DOM too often
				this.currentTimerPercentage = remainderPercentage;
			}
		}
	}

	updateBarTimerText(text = '') {
		this.logger.debug(`[${this.updateBarTimerText.name}]`, { text });

		const $barTimerText = this.$('.bar-timer-text');
		if (text && $barTimerText.text() !== text) {
			$barTimerText.text(text);
		}
	}

	onTimeLow() {
		this.logger.log(`[${this.onTimeLow.name}]`);

		AudioManager.playSound(SOUNDS.timerLow);
		this.$('.timer').addClass('time-low');
	}

	onTimeExpired() {
		this.logger.log(`[${this.onTimeExpired.name}]`);

		AudioManager.playSound(SOUNDS.timerExpired);
		this.wait(2000).then(() => this.game.nextRound());
	}

	onTimerReset() {
		this.logger.log(`[${this.onTimerReset.name}]`);
		this.$('.timer').removeClass('time-low');
	}

	toggleClock() {
		this.logger.log(`[${this.toggleClock.name}]`);

		if (!Environment.isProd() && this.game && this.game.clock) {
			if (this.game.clock.isRunning()) {
				this.game.clock.stop();
			} else {
				this.game.clock.start();
			}
		}
	}

	skipToEnd() {
		this.logger.log(`[${this.skipToEnd.name}]`);

		if (!Environment.isProd() && this.game && this.game.rounds) {
			this.logger.info('[skipToEnd] successful');
			AudioManager.playSound('triviaCorrect');
			this.cleanUpVideo();
			this.game.trigger(EVENTS.gameComplete);
		}
	}

	displayEndScreen() {
		this.logger.log(`[${this.displayEndScreen.name}]`);

		this.game.clock?.stop();

		// Get endscreen data from content.
		const endscreenData = this.game.getEndscreenData() || {};
		const { [ATTRIBUTES.scoreType]: scoreType, [ATTRIBUTES.pages]: pages = [] } =
			this.game.attributes;

		const score = Math.round(this.game.getScore()) || 0;
		const possibleScore = Math.round(this.game.getPossibleScore()) || 0;

		let scoreText = '';
		let possibleScoreText = '';

		if (scoreType === SCORE_TYPES.timed) {
			scoreText = msToMinutesSeconds(score || 0);
			possibleScoreText = msToMinutesSeconds(possibleScore || 0);
		} else {
			scoreText = score.toLocaleString();
			possibleScoreText = possibleScore.toLocaleString();
		}

		const templateData = {
			...endscreenData,
			score: scoreText,
			possibleScore: possibleScoreText,
			pages: pages.map((page) => {
				if (typeof page === 'string') {
					return Arcade.instance?.getPage(page);
				}
				return page;
			}),
		};

		// Check for any social media shares that need to be setup for a specific endscreen
		templateData.pages?.forEach((page) => {
			if (page?.uri && SOCIAL_SHARE_REGEX.test(page.uri)) {
				const [, , id] = SOCIAL_SHARE_REGEX.exec(page.uri);
				if (!id) {
					// No ID provided. Assuming there's a specific share created for this endscreen.
					page.uri += templateData.id;
				}
			}
		});

		return this.replaceView(this.endscreenTemplate(templateData), () => {
			const btns = createButtons(templateData.pages);
			this.$('.btns').empty().append(btns);

			// Add class name to allow custom background targeting in css
			this.$el.addClass('endscreen');
			InputManager.focus();
			this.attachDecorators(); // needs to be called after adding the buttons
		})
			.then(() => this.wait()) // Helps slower device refocus after replacing the view
			.then(() => InputManager.focus());
	}

	/**
	 * Restarts the current game. Typically triggered by the 'reset' button on the endscreen
	 *
	 * @param {JQuery.TriggeredEvent} [event] - Event from button click
	 * @returns {Promise<any>}
	 */
	restartGame(event) {
		this.logger.log(`[${this.restartGame.name}]`, { event });

		if (event) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}

		if (this.game) {
			this.game.sendGA4Event('GameRestart');
			this.game.destroy();
		}

		return this.dismissView().then(() => {
			this.$el.removeClass('endscreen');
			return this.initialize({ ...this.options });
		});
	}

	destroy() {
		InputManager.off('9', this.skipToEnd);

		this.game.destroy();
		super.destroy();
	}
}
